/**
 * Utilitaires d'accessibilité
 */

/**
 * Piège le focus dans un élément (pour modals/dialogs)
 * @param {HTMLElement} element
 * @returns {Function} - Fonction de nettoyage
 */
function getFocusableElements(root) {
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const elements = Array.from(root.querySelectorAll('*'));
  const focusable = [];

  for (const el of elements) {
    if (el.shadowRoot) {
      focusable.push(...getFocusableElements(el.shadowRoot));
    } else if (el.matches(focusableSelectors)) {
      focusable.push(el);
    }
  }
  return focusable;
}

export function trapFocus(element) {
  const focusableElements = getFocusableElements(element);
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  function handleTab(e) {
    if (e.key !== 'Tab') return;

    const activeElement = e.composedPath()[0];

    if (e.shiftKey) {
      if (activeElement === firstFocusable) {
        lastFocusable?.focus();
        e.preventDefault();
      }
    } else {
      if (activeElement === lastFocusable) {
        firstFocusable?.focus();
        e.preventDefault();
      }
    }
  }

  element.addEventListener('keydown', handleTab);
  firstFocusable?.focus();

  return () => {
    element.removeEventListener('keydown', handleTab);
  };
}

/**
 * Annonce un message aux lecteurs d'écran
 * @param {string} message
 * @param {string} [priority='polite'] - 'polite' ou 'assertive'
 */
export function announceToScreenReader(message, priority = 'polite') {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', priority);
  announcement.className = 'visually-hidden';
  announcement.textContent = message;

  document.body.appendChild(announcement);

  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
}

/**
 * Vérifie si l'utilisateur préfère un mouvement réduit
 * @returns {boolean}
 */
export function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Ajoute un ID unique pour aria-describedby
 * @param {string} [prefix='desc']
 * @returns {string}
 */
export function generateAriaId(prefix = 'desc') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
